import {
  Box,
  Typography,
  Paper,
  Alert,
  TextField,
  Autocomplete,
  Button,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Tooltip,
} from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import RemoveIcon from '@mui/icons-material/Remove';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { createRecipeActivitySubmitValidation } from './activityValidation';
import { RecipeFileUploadArea } from './RecipeFileUploadArea';
import { Footer } from '@/components/details/Footer';
import ReadOnlyFooter from '@/components/details/ReadOnlyFooter';
import BasicActivityInfo from '@/components/details/BasicActivityInfo';
import {
  ActivityMaterialDto,
  SampleActivityDto,
  PrivilegeEnum,
  AvailableTemplate,
  RecipeFavoriteDto,
  StatusDto,
  UpdateSampleActivityRequest,
  ActivityCategoryDto,
  ActivityTypeDto,
} from '@/api/ApiClient';
import Recipe from '@/components/recipe/Recipe';
import { RecipeFullViewDialog } from '@/components/recipe/RecipeFullViewDialog';
import { RecipeTemplateDto, RecipeState, ColumnType } from '@/components/recipe';
import { log } from '@/lib/utils/logger';
import { nodeIsReadOnlyForUi } from '@/lib/tree/treeNodeTypeConfigs';
import { getProblemDetail } from '@/lib/utils/apiErrors';

import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import DetailField from '@/components/common/DetailField';
import { OutlinedPaper, FlexWrapBox } from '@/components/common/StyledComponents';
import { useTree } from '@/hooks/useTree';
import { TreeNodeFactory } from '@/lib/tree/treeNodeFactory';
import { useTreeSelection } from '@/hooks/useNodeState';
import { useIsDataModified, useParentSampleData, useSampleActivityData } from '@/hooks/useTreeDetailSubscriptions';
import { useBackend } from '@/hooks/useBackend';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useUserResolution } from '@/lib/options/useUserResolution';
import { useDialogStore } from '@/stores/dialogStore';
import { updateSampleActivityWithPreconditionRetry } from '@/lib/tree/submitEntityUtils';
import { mapRecipeDtoToState, mapRecipeStateToDto, mapTemplateToRecipeState } from '@/lib/types/recipeMapper';
import { Lookup } from '@/lib/constants/lookup';
import { getCachedOption } from '@/lib/options/optionsCache';
import { convertDateForBackend } from '@/lib/utils/dateUtils';
import { toRecipeTemplateStep } from '@/lib/utils/recipeTemplateStep';
import {
  getRecipeTemplateWithTechniqueFallback,
  primaryRecipeTemplateTechnique,
} from '@/lib/utils/recipeTemplateLookup';
import { buildTechniqueOptions, transformOptions } from '@/lib/utils/optionUtils';
import { filterAutocompleteOptionsByMatchRank } from '@/lib/utils/autocompleteMatchRank';
import { extractSampleStartingMaterials } from '@/lib/utils/sampleUtils';
import {
  enrichRecipeStateWithLookupValues,
  checkStartingMaterialRequirements,
  checkPreparationInstrumentRequirement,
  isRecipeFileUploadTemplate,
  isRecipeDataEmpty,
} from '@/lib/utils/recipeUtils';
import { IsCompounding, IsCVT, IsFloatingZone } from '@/lib/utils/bulkTechniqueFilters';
import { useSubmitWithValidation } from '@/hooks/useSubmitWithValidation';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { useRecipeEditor } from '@/components/recipe/hooks/useRecipeEditor';
import {
  useRecipeActivityRuntime,
  useRecipeRuntimeCalculatedDelegate,
} from '@/components/recipe/hooks/useRecipeActivityRuntime';
import { RecipeActivityRuntimeField } from '@/components/recipe/RecipeActivityRuntimeField';
import { shouldSyncActivityRuntimeFromRecipe } from '@/lib/utils/recipeRuntime';
import { validateRequiredToSave } from '@/lib/utils/saveValidation';
import { createProcessingActivitySaveValidation } from '@/lib/validation/saveValidationBuilders';
import { RecipeInitDialog, type RecipeInitDialogMode } from '@/components/dialog/RecipeInitDialog';
import { resolveFavoriteWithRecipe } from '@/lib/utils/recipeFavoriteAtCreate';

const OPTION_KEYS: OptionKey[] = ['materials', 'synthesisTechniquesLeafOnly', 'synthesisInstruments'];
const BULK_RAW_MATERIALS_KEY: OptionKey[] = ['bulkRawMaterials'];
const EMPTY_ARRAY: OptionKey[] = [];

/** Ordered editor rows; one placeholder row when there is no persisted material yet (matches legacy first layer). */
function getEditorMaterialRows(materials: ActivityMaterialDto[] | undefined): ActivityMaterialDto[] {
  if (materials != null && materials.length > 0) {
    return [...materials]
      .sort((a, b) => (a.layer ?? 0) - (b.layer ?? 0))
      .map((m, i) => ({
        ...m,
        // 1-based layers (legacy); treat missing or invalid 0 as position index
        layer: m.layer != null && m.layer >= 1 ? m.layer : i + 1,
        asSampleMaterial: m.asSampleMaterial ?? true,
      }));
  }
  return [{ layer: 1, asSampleMaterial: true }];
}

function persistMaterialEditorRows(rows: ActivityMaterialDto[]): ActivityMaterialDto[] {
  return rows.map((row, index) => ({
    id: row.id,
    layer: index + 1,
    asSampleMaterial: row.asSampleMaterial ?? true,
  }));
}

/** Only rows with a material id are persisted to the API (legacy populate). */
function materialsForSaveApi(materials: ActivityMaterialDto[] | undefined): ActivityMaterialDto[] | undefined {
  if (materials === undefined) return undefined;
  const withIds = materials.filter((m) => m.id != null && !Number.isNaN(Number(m.id)));
  return withIds.map((m, idx) => ({
    id: Number(m.id),
    layer: m.layer ?? idx + 1,
    asSampleMaterial: m.asSampleMaterial ?? true,
  }));
}

/** Template name from activity entity (recipe JSON first, then top-level field) — matches useRecipeEditor. */
function getEffectiveRecipeTemplateName(activity: SampleActivityDto | undefined | null): string {
  if (!activity) return '';
  const fromRecipe = activity.recipe?.templateName?.trim();
  const fromField = activity.recipeTemplate?.trim();
  return fromRecipe || fromField || '';
}

/** Section heading: include template name when known; avoid a fake "Untitled" label. */
function formatRecipeSectionTitle(templateName: string | undefined | null): string {
  const name = templateName?.trim();
  return name ? `Recipe: ${name}` : 'Recipe';
}

/**
 * Props for the RecipeActivityDetail component
 */
/**
 * RecipeActivityDetail component for displaying processing activities with recipes
 * Supports SYN, PREP, and POST activity types
 */
const RecipeActivityDetail: FC = () => {
  // State
  const [isManuallyEditing, setIsManuallyEditing] = useState(false);
  const [templateOutdated, setTemplateOutdated] = useState(false);
  const [recipeTemplateOptions, setRecipeTemplateOptions] = useState<AvailableTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingFullTemplate, setLoadingFullTemplate] = useState(false);
  const [recipeInitDialogOpen, setRecipeInitDialogOpen] = useState(false);
  const [recipeInitDialogMode, setRecipeInitDialogMode] = useState<RecipeInitDialogMode>('initialize');

  // FIXME: Multiple refs used for state tracking — see docs/guide/effects-and-async-state.md and docs/guide/recipe-activity-detail.md
  const isUpdatingTemplateRef = useRef(false); // Flag to prevent template version check during update
  const templateCheckedForActivityRef = useRef<string | null>(null); // Track which activity version we've checked
  const hasEnrichedRecipeRef = useRef(false); // Track if recipe has been enriched with lookup values
  const prevRecipeDataRef = useRef<RecipeState | undefined>(undefined); // Track previous recipe data to detect structure changes
  const { options } = useSelectOptions(OPTION_KEYS);
  const { setDataModified, updateData, getSampleActivityData, reloadNode, reloadNodeData, addFiles } = useTree();
  const { selectedNode } = useTreeSelection();

  const backend = useBackend();
  const { resolveUser } = useUserResolution();
  const systemConfig = useSystemConfig();
  const { showSnackbar } = useSnackbar();
  const { submitWithValidation } = useSubmitWithValidation();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Check if this is a readonly node type
  const isReadOnlyNode = selectedNode ? nodeIsReadOnlyForUi(selectedNode.type) : false;

  const activity = useSampleActivityData(selectedNode);

  const sample = useParentSampleData(selectedNode);
  const sampleTechniqueId = sample?.techniqueId;

  // Extract startingMaterials from sample for recipe templates
  const sampleStartingMaterials = useMemo(() => extractSampleStartingMaterials(sample), [sample]);

  const effectiveRecipeTemplateName = useMemo(() => getEffectiveRecipeTemplateName(activity), [activity]);

  /**
   * Resolve one canonical template lookup context for all template API calls.
   * PREP/POST generic templates use Substrate only when both activity and sample lack a technique.
   */
  const resolveTemplateContext = useCallback(
    (
      activityType: string | undefined,
      activityTechnique: string | undefined,
      instrument: string | undefined,
      sampleTechniqueId?: string
    ): { technique: string | null; instrument: string | null } => ({
      technique: primaryRecipeTemplateTechnique(activityType, activityTechnique, sampleTechniqueId, instrument),
      instrument: instrument || null,
    }),
    []
  );

  /**
   * Check if a template requires startingMaterials and validate that sample has them.
   * Returns true if validation passes, false if it fails (and shows error to user).
   */
  const validateStartingMaterialsForTemplate = useCallback(
    (template: RecipeTemplateDto): boolean => {
      const startingMaterialCount = sampleStartingMaterials?.length ?? 0;
      const result = checkStartingMaterialRequirements(template, startingMaterialCount);
      if (!result.valid && result.errorMessage) {
        showSnackbar(result.errorMessage, 'error');
      }
      return result.valid;
    },
    [sampleStartingMaterials, showSnackbar]
  );

  const validateInstrumentRequirementForTemplate = useCallback(
    (template: RecipeTemplateDto, activitySnapshot?: Pick<SampleActivityDto, 'type' | 'instrument'>) => {
      const act = activitySnapshot ?? activity;
      if (!act) return true;
      const result = checkPreparationInstrumentRequirement(template, act);
      if (!result.valid && result.errorMessage) {
        showSnackbar(result.errorMessage, 'error');
      }
      return result.valid;
    },
    [activity, showSnackbar]
  );

  const { showConfirmDialog } = useDialogStore();
  const isSubmitted = activity?.status === StatusDto.Submitted;

  // Compute isReadOnly during render - derived from modification state and manual edit mode
  const dataId = selectedNode?.dataId;
  const isModified = useIsDataModified(dataId);
  const isReadOnly = !isModified && !isManuallyEditing;

  /** Recipe grid edits live in MobX (RecipeStore); notify tree dirty so Footer shows Save. */
  const markRecipeGridDirty = useCallback(() => {
    if (!dataId) return;
    setDataModified(dataId, true);
  }, [dataId, setDataModified]);

  const runtimeCalculatedDelegate = useRecipeRuntimeCalculatedDelegate();

  // Shared recipe editor state/store orchestration.
  // Keep template/favorites loading flows local for now; editor/store lifecycle is centralized.
  const recipeEditor = useRecipeEditor({
    activity: activity
      ? {
          type: activity.type,
          technique: activity.technique,
          instrument: activity.instrument,
          recipe: activity.recipe,
          recipeTemplate: effectiveRecipeTemplateName || activity.recipeTemplate,
          status: activity.status,
          sample: activity.sample,
        }
      : undefined,
    isReadOnly,
    autoLoadTemplates: true,
    onRecipeInputChange: markRecipeGridDirty,
    onRuntimeCalculated: runtimeCalculatedDelegate.delegate,
  });
  const {
    recipeData,
    setRecipeData,
    fullRecipeTemplate,
    setFullRecipeTemplate,
    recipeStore,
    isInitializingRecipeStore,
    recipeTemplateDTO,
    canEdit,
    checkTemplateOutdated,
    applyLatestTemplateUpdate,
    requestRecreateStore,
  } = recipeEditor;

  const isPrepOrPostActivity =
    activity?.type === ActivityTypeDto.Preparation || activity?.type === ActivityTypeDto.PostTreatment;

  const templateContextForApi = useMemo(
    () => resolveTemplateContext(activity?.type, activity?.technique, activity?.instrument, sampleTechniqueId),
    [activity?.type, activity?.technique, activity?.instrument, sampleTechniqueId, resolveTemplateContext]
  );

  const lockTechniqueInstrumentOnRecipePanel = isReadOnly || isSubmitted;

  // Material options
  const materialOptions = useMemo(
    () => transformOptions(options.materials, (m) => !m.isSubstrate),
    [options.materials]
  );

  /** Match legacy chooseMaterial: template.definesMaterial, or show if activity already has materials when template is missing. */
  const showMaterialSection = useMemo(() => {
    if (!activity) return false;
    if (fullRecipeTemplate != null) {
      return fullRecipeTemplate.definesMaterial === true;
    }
    return (activity.materials?.filter((m) => m.id != null).length ?? 0) > 0;
  }, [activity, fullRecipeTemplate]);

  /** Read-only: top layer first, one line per layer (no duplicate “A on B” line). */
  const materialReadonlySummary = useMemo(() => {
    if (!activity) return 'N/A';
    const mats = activity.materials?.filter((m) => m.id != null) ?? [];
    if (mats.length === 0) return 'N/A';

    const byLayerAsc = [...mats].sort((a, b) => (a.layer ?? 0) - (b.layer ?? 0));
    const effectiveLayer = (m: ActivityMaterialDto) => {
      const ix = byLayerAsc.indexOf(m);
      return m.layer != null && m.layer >= 1 ? m.layer : ix + 1;
    };
    const materialName = (m: ActivityMaterialDto) =>
      materialOptions.find((o) => String(o.id) === String(m.id))?.name ?? String(m.id);

    const topFirst = [...byLayerAsc].reverse();

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {topFirst.map((m, i) => {
          const layerNum = effectiveLayer(m);
          const isSample = m.asSampleMaterial ?? true;
          const nameHtml = materialName(m);
          return (
            <Typography key={`${m.id}-${i}`} variant="body2" component="div" sx={{ '& sub, & sup': { lineHeight: 1 } }}>
              <Box component="span">{layerNum}: </Box>
              <Box component="span" dangerouslySetInnerHTML={{ __html: nameHtml }} />
              {!isSample ? ' (not sample material)' : null}
            </Typography>
          );
        })}
      </Box>
    );
  }, [activity, materialOptions]);

  const editorMaterialRows = useMemo(() => getEditorMaterialRows(activity?.materials), [activity?.materials]);

  // Helper function to populate lookup dropdown values directly in RecipeState
  const enrichRecipeStateWithLookupValuesWrapper = useCallback(
    (state: RecipeState, materials: typeof bulkOptions): RecipeState => {
      return enrichRecipeStateWithLookupValues(state, materials?.bulkRawMaterials);
    },
    []
  );

  // ============================================================================
  // LEGACY FEATURE: Lazy-load bulk raw materials for recipe lookup columns
  // ============================================================================
  // Check if any lookup columns exist in the template (for new recipes) or existing recipe
  const hasLookupColumns = useMemo(() => {
    // Check new recipe templates
    if (
      fullRecipeTemplate?.groups?.some((group) =>
        group.columns?.some((col) => col.type === ColumnType.Lookup && col.lookupValues)
      )
    ) {
      return true;
    }

    // Check existing recipes
    if (
      activity?.recipe?.groups?.some((group) =>
        group.columns?.some((col) => col.type === ColumnType.Lookup && col.lookupValues)
      )
    ) {
      return true;
    }

    return false;
  }, [fullRecipeTemplate, activity?.recipe]);

  // Lazy-load bulk raw materials ONLY if template has lookup columns
  const { options: bulkOptions, loading: bulkMaterialsLoading } = useSelectOptions(
    hasLookupColumns ? BULK_RAW_MATERIALS_KEY : EMPTY_ARRAY
  );

  // Watch: recipeData
  // Why: Need to detect when recipe structure changes (new recipe loaded) vs just enrichment updates
  // Action: Reset enrichment flag when a new recipe is loaded (different structure)
  useEffect(() => {
    if (recipeData !== prevRecipeDataRef.current) {
      // Check if this is a new recipe (not just enrichment)
      const isNewRecipe =
        !prevRecipeDataRef.current ||
        recipeData?.groups.length !== prevRecipeDataRef.current?.groups.length ||
        recipeData?.groups[0]?.symbol !== prevRecipeDataRef.current?.groups[0]?.symbol;

      if (isNewRecipe) {
        hasEnrichedRecipeRef.current = false;
      }
      prevRecipeDataRef.current = recipeData;
    }
  }, [recipeData]);

  // Watch: recipeData, isReadOnly, bulkMaterialsLoading, bulkOptions
  // Why: Recipe lookup columns need dropdown values populated from bulk raw materials when materials finish loading
  // Action: Enrich recipe data with filtered bulk material options for lookup columns (only in edit mode, only once)
  useEffect(() => {
    if (!recipeData || isReadOnly || bulkMaterialsLoading) return;
    const hasLookups = recipeData.groups.some((g) => g.columns.some((c) => c.isLookup && c.lookupValues));
    if (!hasLookups) return;
    const alreadyEnriched = recipeData.groups.some((g) =>
      g.columns.some((c) => c.isLookup && c.dropdownValues?.length > 0)
    );
    if (alreadyEnriched) return;

    const enriched = enrichRecipeStateWithLookupValuesWrapper(recipeData, bulkOptions);
    if (enriched !== recipeData) setRecipeData(enriched);
  }, [
    recipeData,
    isReadOnly,
    bulkMaterialsLoading,
    bulkOptions,
    enrichRecipeStateWithLookupValuesWrapper,
    setRecipeData,
  ]);

  // Load recipe templates based on activity properties
  // Called directly from event handlers (user changes) and from node change effect (initial load)
  const loadRecipeTemplates = useCallback(
    async (
      activityType: string | undefined,
      activityTechnique: string | undefined,
      sampleTechniqueId: string | undefined,
      activityInstrument: string | undefined
    ) => {
      setFullRecipeTemplate(null);

      if (!backend || !activityType) {
        setRecipeTemplateOptions([]);
        return;
      }

      setLoadingTemplates(true);
      try {
        const templateContext = resolveTemplateContext(
          activityType,
          activityTechnique,
          activityInstrument,
          sampleTechniqueId
        );
        const templates = await backend.recipeTemplate.availableTemplates(
          toRecipeTemplateStep(activityType) ?? null,
          templateContext.technique,
          templateContext.instrument
        );
        setRecipeTemplateOptions(templates);
      } catch (error) {
        log.error('[RecipeActivityDetail] Failed to load recipe templates:', error);
        showSnackbar('Failed to load recipe templates', 'error');
      } finally {
        setLoadingTemplates(false);
      }
    },
    [backend, resolveTemplateContext, showSnackbar, setFullRecipeTemplate]
  );

  // Reload templates when node changes or is reloaded (dataVersion increments)
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!selectedNode) return;
      const currentActivity = getSampleActivityData(selectedNode);
      if (!currentActivity) return;

      await loadRecipeTemplates(
        currentActivity.type,
        currentActivity.technique,
        sampleTechniqueId,
        currentActivity.instrument
      );
      const templateNameOnActivity = getEffectiveRecipeTemplateName(currentActivity);

      if (!currentActivity.recipe && backend && templateNameOnActivity) {
        setLoadingFullTemplate(true);
        try {
          const template = await getRecipeTemplateWithTechniqueFallback(backend, {
            activityType: currentActivity.type,
            activityTechnique: currentActivity.technique,
            sampleTechniqueId: sampleTechniqueId,
            instrument: currentActivity.instrument,
            templateName: templateNameOnActivity,
          });
          if (!cancelled && template) {
            setFullRecipeTemplate(template);
          }
        } catch (error: any) {
          log.warn('[RecipeActivityDetail] Failed to load template for upload template', error);
        } finally {
          if (!cancelled) {
            setLoadingFullTemplate(false);
          }
        }
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [
    selectedNode,
    selectedNode?.dataVersion,
    selectedNode?.dataId,
    getSampleActivityData,
    loadRecipeTemplates,
    sampleTechniqueId,
    backend,
    setFullRecipeTemplate,
  ]);

  // Technique options (leaf-only for activities)
  const techniqueOptions = useMemo(
    () => buildTechniqueOptions(options.synthesisTechniquesLeafOnly),
    [options.synthesisTechniquesLeafOnly]
  );

  // Instrument options - filtered by selected technique
  const instrumentOptions = useMemo(() => {
    const base = options.synthesisInstruments ?? [];
    const filtered = activity?.technique
      ? base.filter((inst) => inst.synthesisTechniques?.includes(activity.technique!))
      : base;
    return transformOptions(filtered);
  }, [options.synthesisInstruments, activity]);

  /**
   * Edit mode: always show instrument (prep can be furnace/annealing etc.).
   * Readonly: hide prep/post when unset and template does not require an instrument (avoid "N/A").
   */
  const showInstrumentField = useMemo(() => {
    if (!activity) return false;
    if (!lockTechniqueInstrumentOnRecipePanel) return true;
    if (recipeTemplateDTO?.usesInstrument === true) return true;
    if (activity.instrument) return true;
    if (!isPrepOrPostActivity) return true;
    return false;
  }, [activity, recipeTemplateDTO?.usesInstrument, isPrepOrPostActivity, lockTechniqueInstrumentOnRecipePanel]);

  const techniqueReadonlyDisplay = useMemo(() => {
    const techniqueId =
      activity?.technique ??
      sampleTechniqueId ??
      (isPrepOrPostActivity && !activity?.instrument ? 'Substrate' : undefined);
    const fromOptions = techniqueOptions.find((opt) => opt.id === techniqueId)?.name;
    if (fromOptions) return fromOptions;
    if (techniqueId) return techniqueId;
    return 'N/A';
  }, [activity?.technique, activity?.instrument, sampleTechniqueId, isPrepOrPostActivity, techniqueOptions]);

  // Clear field errors when user starts editing
  const clearFieldError = useCallback((fieldKey: string) => {
    setFieldErrors((prev) => {
      if (prev[fieldKey]) {
        const newErrors = { ...prev };
        delete newErrors[fieldKey];
        return newErrors;
      }
      return prev;
    });
  }, []);

  const handleModification = useCallback(
    (modified: Partial<SampleActivityDto>) => {
      if (!selectedNode) return;

      // Get current data fresh from selectedNode.data to avoid stale closure
      const currentData = getSampleActivityData(selectedNode);
      if (!currentData) return;

      // Update data and mark as modified
      const updated = { ...currentData, ...modified };
      updateData(selectedNode.dataId, updated);
      setDataModified(selectedNode.dataId, true);

      // Clear field errors when user starts editing (a cleared field is `undefined`, so check the key).
      if ('instrument' in modified) {
        clearFieldError('instrument');
      }
      if ('runtime' in modified) {
        clearFieldError('runtime');
      }
    },
    [selectedNode, getSampleActivityData, updateData, setDataModified, clearFieldError]
  );

  const applyMaterialRows = useCallback(
    (rows: ActivityMaterialDto[]) => {
      if (!rows.length) {
        handleModification({ materials: [] });
        return;
      }
      handleModification({ materials: persistMaterialEditorRows(rows) });
    },
    [handleModification]
  );

  // Check if this is a valid recipe activity
  const isValidRecipeActivity = useMemo(() => {
    if (!activity) return false;

    // Must be processing category
    if (activity.category !== ActivityCategoryDto.Processing) return false;

    // Must be one of the supported types
    if (
      activity.type !== ActivityTypeDto.Synthesis &&
      activity.type !== ActivityTypeDto.Preparation &&
      activity.type !== ActivityTypeDto.PostTreatment
    )
      return false;

    // Recipe is optional - can be created later
    return true;
  }, [activity]);
  const instrumentRequiredForSubmit = useMemo(() => {
    if (!activity) return false;
    if (activity.type === ActivityTypeDto.Preparation) {
      return recipeTemplateDTO?.usesInstrument === true;
    }
    return !!activity.technique;
  }, [activity, recipeTemplateDTO?.usesInstrument]);

  const effectiveReadOnly = isReadOnly || isSubmitted || !canEdit;

  const syncRuntimeFromRecipe = shouldSyncActivityRuntimeFromRecipe({
    instrument: activity?.instrument,
    usesInstrument: recipeTemplateDTO?.usesInstrument,
  });

  const handleRuntimeChange = useCallback(
    (runtime: number | undefined) => {
      handleModification({ runtime });
    },
    [handleModification]
  );

  const runtimeField = useRecipeActivityRuntime({
    enabled: !effectiveReadOnly && syncRuntimeFromRecipe,
    onRuntimeChange: handleRuntimeChange,
    runtimeChangedManuallyInitial: activity?.recipe?.runtimeChangedManualy ?? false,
    recipeStore,
    runtimeUnit: fullRecipeTemplate?.runtimeUnit ?? recipeTemplateDTO?.runtimeUnit,
  });
  runtimeCalculatedDelegate.bind(runtimeField.onRuntimeCalculated);

  // Full-screen recipe popup: lets large recipes be worked on without the cramped inline panel.
  const [recipePopupOpen, setRecipePopupOpen] = useState(false);

  // Heuristic for "big": total visible rows across groups. Below this the inline view is comfortable,
  // so the expand button is offered as a convenience rather than auto-suggested.
  const LARGE_RECIPE_ROW_THRESHOLD = 12;
  const recipeRowCount = useMemo(() => {
    const groups = recipeStore?.groups ?? recipeData?.groups ?? [];
    return groups.reduce((sum, group) => sum + (group.rows?.length ?? 0), 0);
  }, [recipeStore, recipeData]);
  const isLargeRecipe = recipeRowCount >= LARGE_RECIPE_ROW_THRESHOLD;

  // Create empty recipe from template
  const handleCreateEmptyRecipeFromTemplate = useCallback(
    (template: RecipeTemplateDto): boolean => {
      // Validate startingMaterials before loading template
      if (!validateStartingMaterialsForTemplate(template)) {
        return false; // Validation failed, error message already shown to user
      }
      if (!validateInstrumentRequirementForTemplate(template)) {
        return false;
      }

      // ============================================================================
      // LEGACY FEATURE: Dynamic Lookup Population
      // ============================================================================
      // Populate lookup dropdowns with bulk raw materials filtered by technique.
      // NOTE: This feature should be REMOVED in the future. New recipes should use
      // proper lookup tables or predefined dropdown values instead of dynamic filtering.
      // ============================================================================

      // Check if any lookup columns exist
      const hasLookupColumns = template.groups?.some((group) =>
        group.columns?.some((col) => col.type === ColumnType.Lookup && col.lookupValues)
      );

      // Access bulk raw materials from cache directly to avoid dependency issues
      const allBulkRawMaterials = getCachedOption('bulkRawMaterials');

      if (hasLookupColumns && allBulkRawMaterials) {
        // Process each group and column
        for (const group of template.groups || []) {
          for (const column of group.columns || []) {
            if (column.type === ColumnType.Lookup && column.lookupValues) {
              let filteredMaterials: typeof allBulkRawMaterials = [];

              switch (column.lookupValues) {
                case Lookup.RawMaterialCompounding:
                  filteredMaterials = allBulkRawMaterials.filter((m) =>
                    m.techniques?.some((tech) => IsCompounding(tech))
                  );
                  break;

                case Lookup.RawMaterialTransport:
                  filteredMaterials = allBulkRawMaterials.filter((m) => m.techniques?.some((tech) => IsCVT(tech)));
                  break;

                case Lookup.RawMaterialFloatingZone:
                  filteredMaterials = allBulkRawMaterials.filter((m) =>
                    m.techniques?.some((tech) => IsFloatingZone(tech))
                  );
                  break;

                default:
                  filteredMaterials = [];
              }

              // Convert to dropdown format (filter out materials without IDs)
              // Preserve the full object so formulas can access all fields via lookup()
              column.values = filteredMaterials
                .filter((material) => material.id !== undefined)
                .map((material) => ({
                  ...material, // Preserve all fields for lookup() formulas
                  id: material.id!,
                  label: material.name,
                }));

              // Add "-- Add new --" option
              column.values.unshift({
                id: -1,
                label: ' -- Add new -- ',
              } as any);
            }
          }
        }
      }

      const emptyRecipe = mapTemplateToRecipeState(template, sampleStartingMaterials);
      setRecipeData(emptyRecipe);

      // Convert to RecipeDto and mark as dirty
      const recipeDto = mapRecipeStateToDto(emptyRecipe);
      handleModification({
        recipe: recipeDto,
        recipeTemplate: template.name, // Set the template name field
        recipeTemplateHash: template.hash, // Set the template hash
      });
      return true;
    },
    [
      handleModification,
      sampleStartingMaterials,
      validateStartingMaterialsForTemplate,
      validateInstrumentRequirementForTemplate,
      setRecipeData,
    ]
  );

  // Shared function to load and apply a template
  const loadAndApplyTemplate = useCallback(
    async (templateName: string, requireConfirmation: boolean = true): Promise<boolean> => {
      if (!backend || !selectedNode) return false;

      const currentData = getSampleActivityData(selectedNode);
      if (!currentData) return false;

      try {
        // Fetch the full template details from the backend
        const fullTemplate = await getRecipeTemplateWithTechniqueFallback(backend, {
          activityType: currentData.type,
          activityTechnique: currentData.technique,
          sampleTechniqueId: sampleTechniqueId,
          instrument: currentData.instrument,
          templateName: templateName || '',
        });

        if (!fullTemplate) {
          log.error('Failed to load template details');
          showSnackbar('Failed to load template details', 'error');
          return false;
        }

        // If a recipe already exists and confirmation is required, ask for confirmation
        if (requireConfirmation && currentData.recipe) {
          const confirmed = await showConfirmDialog({
            title: 'Replace Recipe',
            message: `A recipe already exists. Do you want to replace it with a new empty recipe based on the "${fullTemplate.name}" template?`,
            confirmText: 'Replace',
            isDestructive: true,
          });

          if (!confirmed) return false;
        }

        // Validate startingMaterials before applying template
        if (!validateStartingMaterialsForTemplate(fullTemplate)) {
          return false; // Validation failed, error already shown
        }
        if (!validateInstrumentRequirementForTemplate(fullTemplate, currentData)) {
          return false;
        }

        // Apply the template
        setFullRecipeTemplate(fullTemplate);
        const applied = handleCreateEmptyRecipeFromTemplate(fullTemplate);
        if (!applied) {
          return false;
        }

        return true;
      } catch (error) {
        log.error('Failed to load and apply template:', error);
        showSnackbar('Failed to load template details', 'error');
        return false;
      }
    },
    [
      backend,
      selectedNode,
      getSampleActivityData,
      showConfirmDialog,
      handleCreateEmptyRecipeFromTemplate,
      showSnackbar,
      validateStartingMaterialsForTemplate,
      validateInstrumentRequirementForTemplate,
      setFullRecipeTemplate,
      sampleTechniqueId,
    ]
  );

  /**
   * Handle technique/instrument changes with template validation
   */
  const handleTechniqueOrInstrumentChange = useCallback(
    async (modified: Partial<SampleActivityDto>) => {
      if (!selectedNode) return;

      const currentData = getSampleActivityData(selectedNode);
      if (!currentData) return;

      const isTechniqueChange = 'technique' in modified;
      const isInstrumentChange = 'instrument' in modified;

      if (!isTechniqueChange && !isInstrumentChange) {
        // Not a technique/instrument change, use regular handler
        handleModification(modified);
        return;
      }

      // A cleared dropdown is sent as `undefined` (not ""), so detect presence via the key, not its value.
      const newTechnique = isTechniqueChange ? modified.technique : currentData.technique;
      const newInstrument = isInstrumentChange ? modified.instrument : currentData.instrument;

      const hasAttachedRecipeContent =
        !!recipeData ||
        !!currentData.recipe ||
        !!(currentData.recipeTemplate && String(currentData.recipeTemplate).trim());

      let needsDestructiveConfirm = false;
      let destructiveTitle = 'Template Not Compatible';
      let destructiveMessage = '';

      if (fullRecipeTemplate && hasAttachedRecipeContent) {
        const templateTechniques = (fullRecipeTemplate.techniques ?? []).filter(
          (t): t is string => typeof t === 'string' && t.trim() !== ''
        );
        const techniqueListRestricts = templateTechniques.length > 0;

        // Clearing technique with an attached recipe/template is destructive; do not treat as compatible.
        const techniqueNonEmpty = String(newTechnique ?? '').trim() !== '';
        const techniqueCompatible =
          techniqueNonEmpty && (!techniqueListRestricts || templateTechniques.includes(String(newTechnique)));

        const instrumentCompatible =
          !newInstrument ||
          fullRecipeTemplate.instrumentTypes?.includes(newInstrument) ||
          fullRecipeTemplate.instruments?.some((inst: any) => inst.id === newInstrument);

        if (!techniqueCompatible || !instrumentCompatible) {
          needsDestructiveConfirm = true;
          destructiveMessage = `The current template "${fullRecipeTemplate.name}" is not compatible with the selected ${isTechniqueChange ? 'technique' : 'instrument'}. The recipe will be cleared. Do you want to continue?`;
        }
      } else if (!fullRecipeTemplate && hasAttachedRecipeContent && isTechniqueChange) {
        // Metadata not loaded yet (or failed), but there is recipe/template state — still guard technique changes.
        needsDestructiveConfirm = true;
        destructiveTitle = 'Change technique?';
        destructiveMessage =
          'Changing the technique may invalidate the current recipe or template selection. The recipe will be cleared. Do you want to continue?';
      }

      if (needsDestructiveConfirm) {
        const confirmed = await showConfirmDialog({
          title: destructiveTitle,
          message: destructiveMessage,
          confirmText: 'Continue',
          isDestructive: true,
        });

        if (!confirmed) {
          return;
        }

        setRecipeData(undefined);
        setFullRecipeTemplate(null);
        setRecipeTemplateOptions([]);

        handleModification({
          ...modified,
          recipe: undefined,
          recipeTemplate: undefined,
          recipeTemplateHash: undefined,
        });

        const clearedActivity = {
          ...currentData,
          ...modified,
          recipe: undefined,
          recipeTemplate: undefined,
          recipeTemplateHash: undefined,
        };
        await loadRecipeTemplates(
          clearedActivity.type,
          clearedActivity.technique,
          sampleTechniqueId,
          clearedActivity.instrument
        );
        return;
      }

      // Apply the technique/instrument change
      handleModification(modified);

      // Reload templates for new technique/instrument (user-initiated change)
      const updatedActivity = { ...currentData, ...modified };
      await loadRecipeTemplates(
        updatedActivity.type,
        updatedActivity.technique,
        sampleTechniqueId,
        updatedActivity.instrument
      );
    },
    [
      selectedNode,
      getSampleActivityData,
      recipeData,
      fullRecipeTemplate,
      showConfirmDialog,
      handleModification,
      loadRecipeTemplates,
      setRecipeData,
      setFullRecipeTemplate,
      sampleTechniqueId,
    ]
  );

  // Watch: activity, recipeTemplateDTO, recipeTemplateOptions.length, recipeData
  // Why: When a template becomes available and there's exactly one option for a new recipe, create empty recipe from it
  // Action: Create empty recipe structure from template if conditions are met (legacy, only when in edit mode)

  // Check if template is outdated once per activity when it becomes editable
  // backend, templateCheckedForActivityRef, and isUpdatingTemplateRef are used directly from closure
  // Note: Using useCallback (not useEffectEvent) because this function is called from both:
  //       1. Event handlers (setIsReadOnly handler when entering edit mode)
  //       2. Effects (when templates finish loading)
  //       useEffectEvent can only be called from effects/effect events, so we use useCallback here
  const checkTemplateVersion = useCallback(
    async (
      currentActivity: typeof activity,
      nodeVersion: number | undefined,
      loading: boolean,
      options: typeof recipeTemplateOptions,
      readonly: boolean
    ) => {
      const activityId = currentActivity?.id?.toString();
      const checkKey = `${activityId}-${nodeVersion}`;

      // Reset check when switching activities or node is reloaded
      if (templateCheckedForActivityRef.current !== checkKey) {
        templateCheckedForActivityRef.current = null;
        setTemplateOutdated(false);
      }

      // Only check if we haven't already checked for this activity version
      if (!activityId || templateCheckedForActivityRef.current === checkKey) {
        return;
      }

      // Only check if we're in edit mode
      if (readonly) {
        return;
      }

      // Only check if we have a recipe with a template
      const hasRecipe = currentActivity?.recipe != null;
      const templateName = getEffectiveRecipeTemplateName(currentActivity);
      if (!hasRecipe || !templateName || !backend || loading) {
        setTemplateOutdated(false);
        return;
      }

      // Skip if we're currently updating the template
      if (isUpdatingTemplateRef.current) return;

      // Mark as checked for this activity version
      templateCheckedForActivityRef.current = checkKey;

      // If templateHash is undefined, treat as outdated (legacy recipes)
      if (!currentActivity.recipeTemplateHash) {
        setTemplateOutdated(true);
        return;
      }

      // If template not in available options, don't fetch - just keep what we have
      const templateAvailable = options.some((opt) => opt.name === templateName);
      if (!templateAvailable) {
        setTemplateOutdated(false);
        return;
      }

      const templateContext = resolveTemplateContext(
        currentActivity.type,
        currentActivity.technique,
        currentActivity.instrument,
        sampleTechniqueId
      );
      const outdated = await checkTemplateOutdated({
        activityType: currentActivity.type,
        technique: currentActivity.technique,
        sampleTechnique: sampleTechniqueId,
        instrument: templateContext.instrument || undefined,
        templateName,
        storedHash: currentActivity.recipeTemplateHash || undefined,
        availableTemplateNames: options.map((opt) => opt.name || '').filter((n) => n),
        requireTemplateInOptions: true,
      });
      setTemplateOutdated(outdated);
    },
    [backend, resolveTemplateContext, checkTemplateOutdated, sampleTechniqueId]
  );

  // Watch: loadingTemplates, recipeTemplateOptions.length
  // Why: Reacts to backend response (templates finish loading while in edit mode)
  //      Need to check if recipe's template is outdated when templates finish loading (if already in edit mode)
  //      Note: Entering edit mode is handled directly in setIsReadOnly handler above
  // Action: Compare recipe's template hash with current template hash from backend, show update prompt if outdated
  // Note: Only checks once per activity version (tracked by ref, reset in handleNodeChange) and only in edit mode
  //       Activity/node/isReadOnly changes don't trigger check - activity/node handled in handleNodeChange,
  //       isReadOnly handled in setIsReadOnly handler. We only check when templates finish loading.
  //       checkTemplateVersion already encapsulates the one-check-per-activity guard via refs.
  useEffect(() => {
    const maybeCheckTemplateVersion = async () => {
      if (!loadingTemplates && !isReadOnly && recipeTemplateOptions.length > 0) {
        await checkTemplateVersion(
          activity,
          selectedNode?.dataVersion,
          loadingTemplates,
          recipeTemplateOptions,
          isReadOnly
        );
      }
    };
    void maybeCheckTemplateVersion();
  }, [loadingTemplates, recipeTemplateOptions, isReadOnly, checkTemplateVersion, activity, selectedNode?.dataVersion]);

  // Recipe store lifecycle is handled by useRecipeEditor.

  // Update recipe to use latest template
  const handleUpdateTemplate = useCallback(async () => {
    if (!activity?.recipe || !backend) {
      log.warn('[RecipeActivityDetail] Cannot update template: missing recipe or backend');
      return;
    }

    try {
      // Set flag to prevent template check during update
      isUpdatingTemplateRef.current = true;

      const templateContext = resolveTemplateContext(
        activity.type,
        activity.technique,
        activity.instrument,
        sampleTechniqueId
      );
      const result = await applyLatestTemplateUpdate({
        activityType: activity.type,
        technique: activity.technique,
        sampleTechnique: sampleTechniqueId,
        instrument: templateContext.instrument || undefined,
        templateName: activity.recipe.templateName || undefined,
        currentData: recipeData,
        sampleStartingMaterials,
      });

      if (!result.success || !result.latestTemplate || !result.mergedRecipe) {
        showSnackbar('Template not found', 'error');
        return;
      }
      const recipeDto = mapRecipeStateToDto(result.mergedRecipe);
      recipeDto.id = activity.recipe.id; // Preserve original ID
      recipeDto.templateHash = result.latestTemplate.hash;
      recipeDto.templateName = result.latestTemplate.name;
      handleModification({
        recipe: recipeDto,
        recipeTemplateHash: result.latestTemplate.hash,
      });

      setTemplateOutdated(false);
      showSnackbar('Recipe updated to latest template', 'success');
    } catch (error) {
      log.error('[RecipeActivityDetail] Failed to update template:', error);
      showSnackbar('Failed to update template', 'error');
    } finally {
      // Reset flag to allow template checks again
      isUpdatingTemplateRef.current = false;
    }
  }, [
    activity,
    sampleTechniqueId,
    recipeData,
    handleModification,
    resolveTemplateContext,
    showSnackbar,
    sampleStartingMaterials,
    applyLatestTemplateUpdate,
    backend,
  ]);

  // Strict recipe import (Upload-type templates only). Files tab uses attach-only uploadActivityFileForm.
  const handleRecipeFileUpload = useCallback(
    async (fileParam: { data: File; fileName: string }) => {
      if (!backend || !activity?.id || !selectedNode) return;
      const uploaded = await backend.sampleActivity.uploadActivityRecipeFileForm(
        activity.id,
        fileParam,
        null,
        null,
        null
      );
      // Match FilesDetails: put returned metadata in the store; reloadNodeData refreshes activity only.
      // reloadNode would call loadFiles, which can serve a stale TreeNodeFactory cache and hide the new file.
      addFiles(selectedNode.dataId, [uploaded]);
      TreeNodeFactory.invalidateCache(`files-${selectedNode.nodeId}`);
      await reloadNodeData(selectedNode.nodeId, backend);
    },
    [backend, activity, selectedNode, addFiles, reloadNodeData]
  );

  // Apply a favorite recipe to the activity; returns false if user cancelled replace confirm.
  const handleFavoriteSelection = useCallback(
    async (favorite: RecipeFavoriteDto | null, skipReplaceConfirm: boolean = false): Promise<boolean> => {
      if (!favorite || !selectedNode) {
        return true;
      }

      const currentData = getSampleActivityData(selectedNode);
      if (!currentData || !backend) return false;

      const loadedFavorite = await resolveFavoriteWithRecipe(backend, favorite);
      if (!loadedFavorite?.recipe) {
        showSnackbar('Could not load recipe from favorite', 'error');
        return false;
      }

      const templateName =
        loadedFavorite.template?.trim() ||
        loadedFavorite.recipe?.templateName?.trim() ||
        currentData.recipeTemplate ||
        '';

      if (!skipReplaceConfirm && currentData.recipe) {
        const existingState = mapRecipeDtoToState(currentData.recipe);
        if (!isRecipeDataEmpty(existingState)) {
          const confirmed = await showConfirmDialog({
            title: 'Replace Recipe',
            message: `A recipe already exists. Replace it with favorite "${loadedFavorite.name}"?`,
            confirmText: 'Replace',
            isDestructive: true,
          });
          if (!confirmed) {
            return false;
          }
        }
      }

      try {
        const recipeState = mapRecipeDtoToState(loadedFavorite.recipe);
        setRecipeData(recipeState);
        const recipeDto = mapRecipeStateToDto(recipeState);
        recipeDto.templateName = templateName || recipeDto.templateName;

        let templateHash: string | undefined;
        if (templateName) {
          const fullTemplate = await getRecipeTemplateWithTechniqueFallback(backend, {
            activityType: currentData.type,
            activityTechnique: currentData.technique,
            sampleTechniqueId: sampleTechniqueId,
            instrument: currentData.instrument,
            templateName,
          });
          if (fullTemplate) {
            setFullRecipeTemplate(fullTemplate);
            templateHash = fullTemplate.hash;
          }
        }

        const modification: Partial<SampleActivityDto> = {
          recipe: recipeDto,
          recipeTemplate: templateName || undefined,
          recipeTemplateHash: templateHash,
        };
        if (loadedFavorite.recipe.calculatedRuntime != null) {
          modification.runtime = loadedFavorite.recipe.calculatedRuntime;
        }
        handleModification(modification);

        showSnackbar(`Applied favorite: ${loadedFavorite.name}`, 'success');
        return true;
      } catch (error) {
        log.error('[RecipeActivityDetail] Failed to apply favorite:', error);
        showSnackbar('Failed to apply favorite', 'error');
        return false;
      }
    },
    [
      selectedNode,
      getSampleActivityData,
      handleModification,
      showSnackbar,
      setRecipeData,
      showConfirmDialog,
      backend,
      setFullRecipeTemplate,
      sampleTechniqueId,
    ]
  );

  /** Apply template or favorite from RecipeInitDialog (dialog already warns on replace). */
  const handleRecipeInitApply = useCallback(
    async (templateName: string, favorite: RecipeFavoriteDto | null): Promise<boolean> => {
      const ok = favorite
        ? await handleFavoriteSelection(favorite, true)
        : await loadAndApplyTemplate(templateName, false);

      if (ok) {
        requestRecreateStore();
        setIsManuallyEditing(true);
        if (!favorite?.recipe) {
          showSnackbar(`Recipe loaded from template "${templateName}"`, 'success');
        }
      }
      return ok;
    },
    [handleFavoriteSelection, loadAndApplyTemplate, requestRecreateStore, showSnackbar]
  );

  const openRecipeInitDialog = useCallback((mode: RecipeInitDialogMode) => {
    setRecipeInitDialogMode(mode);
    setRecipeInitDialogOpen(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!activity?.id || !selectedNode) return;

    try {
      // Force blur on active element to trigger pending onBlur handlers
      const activeElement = document.activeElement as HTMLElement;
      if (activeElement && typeof activeElement.blur === 'function') {
        activeElement.blur();
        // FIXME: Arbitrary 50ms delay for state synchronization — see docs/guide/effects-and-async-state.md
        // Small delay to let React state updates propagate through effects
        await new Promise((resolve) => setTimeout(resolve, 50));
      }

      // CRITICAL: Sync MobX store state to Zustand before saving
      // The store is the source of truth in edit mode, but we save from Zustand
      // Ensure any pending changes in the store are synced to Zustand first
      if (recipeStore && !isReadOnly) {
        const latestRecipeState = recipeStore.toState();
        const recipeDto = mapRecipeStateToDto(latestRecipeState);
        handleModification({ recipe: recipeDto });
        // Also update local recipeData for when store becomes null
        setRecipeData(latestRecipeState);
        // FIXME: Arbitrary 10ms delay for Zustand propagation — see docs/guide/effects-and-async-state.md
        // Small delay to ensure Zustand update propagates
        await new Promise((resolve) => setTimeout(resolve, 10));
      }

      // Get fresh activity data from Zustand (now synced with store)
      const currentActivity = getSampleActivityData(selectedNode);
      if (!currentActivity) return;

      const saveValidation = validateRequiredToSave(createProcessingActivitySaveValidation(currentActivity));
      if (!saveValidation.isValid) {
        setFieldErrors(saveValidation.fieldErrors);
        showSnackbar('Please fill in all required fields', 'error');
        return;
      }

      // Update the activity in the backend.
      const sanitizedMaterials = materialsForSaveApi(currentActivity.materials);
      const request: UpdateSampleActivityRequest = {
        ...currentActivity,
        ...(sanitizedMaterials !== undefined ? { materials: sanitizedMaterials } : {}),
      };
      const preconditionDeps = { showSnackbar, showConfirmDialog };
      const updatedData = await updateSampleActivityWithPreconditionRetry(
        backend,
        currentActivity.id!,
        request,
        preconditionDeps
      );
      if (updatedData === null) return;
      updateData(selectedNode.dataId, updatedData);
      setDataModified(selectedNode.dataId, false);
      setIsManuallyEditing(false); // Exit edit mode after save
      setFieldErrors({});

      // Backend may update sample materials (e.g. materialIds) from activity materials — reload parent sample like StorageActivity
      if (selectedNode.parentId && backend) {
        try {
          await reloadNode(selectedNode.parentId, backend);
          log.debug('[RecipeActivityDetail] Reloaded parent sample after processing activity save');
        } catch (reloadError) {
          log.error('[RecipeActivityDetail] Failed to reload parent sample:', reloadError);
        }
      }

      showSnackbar('Activity saved successfully', 'success');
    } catch (error) {
      log.error('Failed to save activity:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to save activity', 'error');
    }
  }, [
    activity?.id,
    selectedNode,
    recipeStore,
    isReadOnly,
    handleModification,
    getSampleActivityData,
    showSnackbar,
    showConfirmDialog,
    backend,
    updateData,
    setDataModified,
    reloadNode,
    setRecipeData,
  ]);

  // Custom submit handler with validation
  const handleSubmit = useCallback(async (): Promise<boolean> => {
    if (!activity || !selectedNode) return false;
    const validationFields = createRecipeActivitySubmitValidation(activity, {
      templateUsesInstrument: recipeTemplateDTO?.usesInstrument,
    });
    const result = await submitWithValidation(selectedNode, validationFields, 'Recipe Activity');
    if (!result.success && result.fieldErrors) {
      setFieldErrors(result.fieldErrors);
      setIsManuallyEditing(true); // Switch to edit mode
    } else {
      setFieldErrors({});
    }
    return result.success;
  }, [activity, selectedNode, recipeTemplateDTO?.usesInstrument, submitWithValidation]);

  if (!activity) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No activity selected
        </Typography>
      </Box>
    );
  }

  if (!isValidRecipeActivity) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">
          This activity is not a valid recipe activity. Recipe activities must be of category &apos;processing&apos; and
          type &apos;SYN&apos;, &apos;PREP&apos;, or &apos;POST&apos; with a recipe field.
        </Alert>
      </Box>
    );
  }

  const isSharedGrowthActivity = activity?.sampleId != null && sample?.id != null && activity.sampleId !== sample.id;

  const growthRunLabel = activity?.sample?.sampleLabel;
  const growthRunHref = growthRunLabel
    ? `${systemConfig?.basePath}/samples/${encodeURIComponent(growthRunLabel)}`
    : `${systemConfig?.basePath}/samples/${activity?.sampleId}`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, p: 2 }}>
        {isSharedGrowthActivity ? (
          <Alert severity="info" sx={{ mb: 2 }} icon={false}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip label="Shared growth" size="small" color="info" variant="outlined" />
              <Typography variant="body2" component="span">
                This growth activity was used to grow more than this one sample. It belongs to the{' '}
                <Link href={growthRunHref} style={{ fontWeight: 500 }}>
                  {growthRunLabel ?? `growth run`}
                </Link>
                .
              </Typography>
            </Box>
          </Alert>
        ) : null}
        <BasicActivityInfo
          activity={activity}
          isReadOnly={isReadOnly}
          isSubmitted={isSubmitted}
          handleModification={handleModification}
          convertDateForBackend={convertDateForBackend}
          hideVisibility={isReadOnlyNode}
        />
        {/* Recipe-specific fields */}
        <OutlinedPaper elevation={0} variant="outlined" sx={{ mt: 2 }}>
          <FlexWrapBox>
            {/* Technique */}
            <DetailField
              label="Technique"
              value={techniqueReadonlyDisplay}
              isReadOnly={lockTechniqueInstrumentOnRecipePanel}
            >
              <Autocomplete
                options={techniqueOptions}
                getOptionLabel={(option) => option.name}
                value={techniqueOptions.find((opt) => String(opt.id) === String(activity.technique)) || null}
                onChange={(_, newValue) => {
                  handleTechniqueOrInstrumentChange({
                    // No selection means "nothing", not an empty string (would violate the instrument/technique FK).
                    technique: newValue?.id != null ? String(newValue.id) : undefined,
                    instrument: undefined, // Clear instrument when technique changes
                  });
                }}
                autoSelect
                autoHighlight
                renderInput={(params) => <TextField {...params} size="small" placeholder="Select Technique" />}
                fullWidth
              />
            </DetailField>

            {showInstrumentField ? (
              <DetailField
                label="Instrument"
                value={
                  instrumentOptions.find((opt) => opt.id === activity.instrument)?.name || activity.instrument || 'N/A'
                }
                isReadOnly={lockTechniqueInstrumentOnRecipePanel}
                requiredToSubmit={instrumentRequiredForSubmit}
                error={fieldErrors.instrument}
              >
                <Autocomplete
                  options={instrumentOptions}
                  getOptionLabel={(option) => option.name}
                  value={instrumentOptions.find((opt) => String(opt.id) === String(activity.instrument)) || null}
                  onChange={(_, newValue) => {
                    handleTechniqueOrInstrumentChange({
                      // No selection means "nothing", not an empty string (would violate the instrument FK).
                      instrument: newValue?.id != null ? String(newValue.id) : undefined,
                    });
                  }}
                  autoSelect
                  autoHighlight
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Select Instrument"
                      error={!!fieldErrors.instrument}
                      helperText={fieldErrors.instrument}
                    />
                  )}
                  fullWidth
                  disabled={!(activity.technique || isPrepOrPostActivity)}
                />
              </DetailField>
            ) : null}

            <DetailField
              label="Runtime (h)"
              value={activity.runtime ? `${activity.runtime}` : 'N/A'}
              isReadOnly={isReadOnly || isSubmitted}
              requiredToSubmit={!!activity.instrument}
              error={fieldErrors.runtime}
            >
              <RecipeActivityRuntimeField
                embedded
                value={activity.runtime}
                onInputChange={runtimeField.onInputChange}
                showResetButton={runtimeField.showResetButton}
                onResetToRecipe={runtimeField.onResetToRecipe}
                disabled={isReadOnly || isSubmitted}
                error={!!fieldErrors.runtime}
                helperText={fieldErrors.runtime}
              />
            </DetailField>

            {/* Materials (per layer + sample-material flag; legacy RecipeActivity.html) — full row */}
            {showMaterialSection ? (
              <Box sx={{ flexBasis: '100%', width: '100%', minWidth: 0 }}>
                <DetailField
                  label="Materials"
                  value={materialReadonlySummary}
                  isReadOnly={isReadOnly || isSubmitted}
                  width="100%"
                >
                  <Box sx={{ width: '100%', maxWidth: '100%' }}>
                    <Table
                      size="small"
                      sx={{
                        width: '100%',
                        tableLayout: 'fixed',
                        minWidth: { xs: 320, sm: 560 },
                      }}
                    >
                      <colgroup>
                        <col style={{ width: 56 }} />
                        <col />
                        <col style={{ width: 200 }} />
                        <col style={{ width: 52 }} />
                      </colgroup>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ verticalAlign: 'middle', py: 1 }}>Layer</TableCell>
                          <TableCell sx={{ verticalAlign: 'middle', py: 1 }}>Material</TableCell>
                          <TableCell
                            sx={{
                              verticalAlign: 'middle',
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              py: 1,
                            }}
                          >
                            Show as Sample Material
                          </TableCell>
                          <TableCell align="right" sx={{ verticalAlign: 'middle', py: 1, px: 0.5 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {editorMaterialRows.map((row, rowIndex) => {
                          const selectedOpt =
                            row.id != null
                              ? (materialOptions.find((opt) => String(opt.id) === String(row.id)) ?? null)
                              : null;
                          return (
                            <TableRow key={rowIndex}>
                              <TableCell sx={{ verticalAlign: 'middle' }}>
                                {row.layer != null && row.layer >= 1 ? row.layer : rowIndex + 1}
                              </TableCell>
                              <TableCell
                                sx={{
                                  verticalAlign: 'middle',
                                  minWidth: 0,
                                  '& .MuiAutocomplete-root': { width: '100%', maxWidth: '100%' },
                                }}
                              >
                                <Autocomplete
                                  options={materialOptions}
                                  getOptionLabel={(option) => option.name}
                                  filterOptions={(opts, params) =>
                                    filterAutocompleteOptionsByMatchRank(opts, params, (o) => o.name)
                                  }
                                  value={selectedOpt}
                                  onChange={(_, newValue) => {
                                    const next = editorMaterialRows.map((r, i) =>
                                      i === rowIndex
                                        ? {
                                            ...r,
                                            id:
                                              newValue != null
                                                ? typeof newValue.id === 'number'
                                                  ? newValue.id
                                                  : Number(newValue.id)
                                                : undefined,
                                            asSampleMaterial: r.asSampleMaterial ?? true,
                                          }
                                        : r
                                    );
                                    const anyId = next.some((r) => r.id != null);
                                    if (!anyId) {
                                      handleModification({ materials: [] });
                                    } else {
                                      applyMaterialRows(next);
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <TextField {...params} size="small" placeholder="Select a material" />
                                  )}
                                  fullWidth
                                />
                              </TableCell>
                              <TableCell sx={{ verticalAlign: 'middle' }}>
                                <Checkbox
                                  aria-label="Show as sample material"
                                  checked={row.asSampleMaterial ?? true}
                                  disabled={row.id == null}
                                  onChange={(_, checked) => {
                                    const next = editorMaterialRows.map((r, i) =>
                                      i === rowIndex ? { ...r, asSampleMaterial: checked } : r
                                    );
                                    applyMaterialRows(next);
                                  }}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="right">
                                <IconButton
                                  size="small"
                                  aria-label="Remove layer"
                                  onClick={() => {
                                    const next = editorMaterialRows.filter((_, i) => i !== rowIndex);
                                    if (!next.length) {
                                      handleModification({ materials: [] });
                                    } else {
                                      applyMaterialRows(next);
                                    }
                                  }}
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    {!isReadOnly && !isSubmitted ? (
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ mt: 1 }}
                        onClick={() => {
                          applyMaterialRows([...editorMaterialRows, { asSampleMaterial: true }]);
                        }}
                        startIcon={<AddIcon />}
                      >
                        Add layer
                      </Button>
                    ) : null}
                  </Box>
                </DetailField>
              </Box>
            ) : null}
            {/* Template Unavailable Warning */}
            {!isReadOnly && !canEdit && activity.recipe ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Recipe template is unavailable. Editing is disabled to prevent data corruption. Formulas requiring
                template constants (like <code>const[elem]</code>) cannot be evaluated.
              </Alert>
            ) : null}

            {/* Template Update Alert - only show in edit mode */}
            {!isReadOnly && !isSubmitted && templateOutdated ? (
              <Alert
                severity="info"
                action={
                  <Button color="inherit" size="small" onClick={handleUpdateTemplate} startIcon={<RefreshIcon />}>
                    Update Template
                  </Button>
                }
                sx={{ mb: 2 }}
              >
                The recipe template has been updated. Click &quot;Update Template&quot; to use the latest version.
              </Alert>
            ) : null}
          </FlexWrapBox>
        </OutlinedPaper>
        {/* Show recipe component only if we have recipe data, otherwise show template selection or upload area */}
        {!recipeTemplateDTO || !recipeData ? (
          <>
            {/* Prominent upload area for file-upload templates when no recipe exists yet */}
            {fullRecipeTemplate &&
            isRecipeFileUploadTemplate(fullRecipeTemplate) &&
            !activity?.recipe &&
            activity?.id &&
            selectedNode &&
            !isReadOnly &&
            !isSubmitted &&
            backend ? (
              <RecipeFileUploadArea
                activityId={activity.id}
                recipeTemplate={effectiveRecipeTemplateName || fullRecipeTemplate.name || ''}
                onUpload={handleRecipeFileUpload}
                disabled={!effectiveRecipeTemplateName}
              />
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                {loadingTemplates || loadingFullTemplate || (activity?.recipe && !recipeData) ? (
                  <>
                    <CircularProgress sx={{ mb: 2 }} />
                    <Typography variant="body1" color="text.secondary">
                      Loading recipe...
                    </Typography>
                  </>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body1" color="text.secondary">
                      {isReadOnly || isSubmitted
                        ? 'This activity has no recipe. Edit the activity to initialize one from a template.'
                        : 'Initialize a recipe from a template or favorite.'}
                    </Typography>
                    {!isReadOnly && !isSubmitted ? (
                      <Button variant="contained" onClick={() => openRecipeInitDialog('initialize')}>
                        Initialize recipe…
                      </Button>
                    ) : null}
                  </Box>
                )}
              </Box>
            )}
          </>
        ) : loadingFullTemplate && activity?.recipe && !fullRecipeTemplate ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Loading recipe template...
            </Typography>
          </Box>
        ) : !isReadOnly &&
          recipeData.groups.some((group) => group.columns.some((col) => col.isLookup && col.lookupValues)) &&
          !recipeData.groups.some((group) =>
            group.columns.some((col) => col.isLookup && col.dropdownValues.length > 0)
          ) ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Loading recipe data...
            </Typography>
          </Box>
        ) : (
          <Paper sx={{ p: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ flex: 1, minWidth: 0 }}>
                {formatRecipeSectionTitle(effectiveRecipeTemplateName || activity.recipe?.templateName)}
              </Typography>
              {!isReadOnly && !isSubmitted ? (
                <Button size="small" variant="outlined" onClick={() => openRecipeInitDialog('startNew')}>
                  Start new recipe…
                </Button>
              ) : null}
              {recipeStore || recipeData ? (
                <Tooltip
                  title={
                    isLargeRecipe ? 'This recipe is large — open it in a full-screen view' : 'Open in full-screen view'
                  }
                >
                  <Button
                    size="small"
                    variant={isLargeRecipe ? 'outlined' : 'text'}
                    color={isLargeRecipe ? 'primary' : 'inherit'}
                    startIcon={<OpenInFullIcon fontSize="small" />}
                    onClick={() => setRecipePopupOpen(true)}
                  >
                    Full view
                  </Button>
                </Tooltip>
              ) : null}
            </Box>
            {/* Prominent upload area for file-upload templates when recipe is empty - hide once user enters data */}
            {fullRecipeTemplate &&
            isRecipeFileUploadTemplate(fullRecipeTemplate) &&
            isRecipeDataEmpty(recipeStore?.toState() ?? recipeData) &&
            activity?.id &&
            selectedNode &&
            !isReadOnly &&
            !isSubmitted &&
            backend ? (
              <RecipeFileUploadArea
                activityId={activity.id}
                recipeTemplate={effectiveRecipeTemplateName || fullRecipeTemplate.name || ''}
                onUpload={handleRecipeFileUpload}
                disabled={!effectiveRecipeTemplateName}
              />
            ) : null}
            {/* In edit mode: use MobX store, in readonly mode: use plain data */}
            {isInitializingRecipeStore ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '200px',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <CircularProgress size={40} />
                <Typography variant="body2" color="text.secondary">
                  Initializing recipe editor...
                </Typography>
              </Box>
            ) : recipeStore || recipeData ? (
              // While the popup is open, hide (don't unmount) the inline table: this avoids painting a
              // large table twice but keeps this instance mounted as the store owner (it disposes on unmount).
              <Box sx={{ display: recipePopupOpen ? 'none' : undefined }}>
                <Recipe
                  key={activity.id}
                  store={recipeStore || undefined}
                  data={effectiveReadOnly ? recipeData : undefined}
                  readonly={effectiveReadOnly}
                />
              </Box>
            ) : null}
          </Paper>
        )}
      </Box>

      <RecipeFullViewDialog
        open={recipePopupOpen}
        onClose={() => setRecipePopupOpen(false)}
        title={formatRecipeSectionTitle(effectiveRecipeTemplateName || activity?.recipe?.templateName)}
        store={recipeStore || undefined}
        data={effectiveReadOnly ? recipeData : undefined}
        readonly={effectiveReadOnly}
      />

      {activity?.type ? (
        <RecipeInitDialog
          open={recipeInitDialogOpen}
          onClose={() => setRecipeInitDialogOpen(false)}
          mode={recipeInitDialogMode}
          activityType={activity.type}
          technique={templateContextForApi.technique}
          instrument={templateContextForApi.instrument}
          initialTemplateName={effectiveRecipeTemplateName || undefined}
          onApply={handleRecipeInitApply}
        />
      ) : null}

      {isReadOnlyNode ? (
        <ReadOnlyFooter
          lastChangedDate={activity.lastChangedDate}
          lastChangedBy={activity.lastChangedByUser || 'Unknown'}
          status={activity.status || 'Draft'}
        />
      ) : (
        <Footer
          lastChangedDate={activity.lastChangedDate}
          lastChangedBy={resolveUser(activity.lastChangedByUser || '')}
          status={activity.status}
          isReadOnly={isReadOnly}
          setIsReadOnly={(val) => {
            setIsManuallyEditing(!val);
            if (!val) {
              // Entering edit mode - store lifecycle is managed by useRecipeEditor.
              if (!loadingTemplates && recipeTemplateOptions.length > 0) {
                checkTemplateVersion(
                  activity,
                  selectedNode?.dataVersion,
                  loadingTemplates,
                  recipeTemplateOptions,
                  false
                );
              }
            }
          }}
          onSave={handleSave}
          privilege={PrivilegeEnum.ProcessingActivity}
          onSubmit={handleSubmit}
        />
      )}
    </Box>
  );
};

export default RecipeActivityDetail;
